import type { Alert, Category, TerytBoundaryFeature } from "./types";

export const boundariesByTeryt = new Map<string, TerytBoundaryFeature>();
export const voivodeshipBoundariesByTeryt = new Map<string, TerytBoundaryFeature>();
export const countyNameByTeryt = new Map<string, string>();
export const activeCategories = new Set<Category>(["meteorological"]);

export let currentAlerts: Alert[] = [];

export function setCurrentAlerts(alerts: Alert[]): void {
  currentAlerts = alerts;
}

export function countyCodesForScope(alert: Alert): string[] {
  if (alert.scope_kind === "county") {
    return [alert.scope_id];
  }
  if (alert.scope_kind === "voivodeship") {
    return [...boundariesByTeryt.keys()].filter((code) => code.startsWith(alert.scope_id));
  }
  return [];
}

export function alertsForRegion(countyCode: string): Alert[] {
  const voivodeshipPrefix = countyCode.slice(0, 2);
  return currentAlerts.filter((alert) => {
    if (!activeCategories.has(alert.category)) return false;
    if (alert.scope_kind === "county") return alert.scope_id === countyCode;
    if (alert.scope_kind === "voivodeship") return alert.scope_id === voivodeshipPrefix;
    return false;
  });
}

export function alertsForVoivodeship(prefix: string): Alert[] {
  return currentAlerts.filter((alert) => {
    if (!activeCategories.has(alert.category)) return false;
    if (alert.scope_kind === "county") return alert.scope_id.startsWith(prefix);
    if (alert.scope_kind === "voivodeship") return alert.scope_id === prefix;
    return false;
  });
}

export function alertsNationwide(): Alert[] {
  return currentAlerts.filter((alert) => activeCategories.has(alert.category));
}

export function sortAlerts(alerts: Alert[]): Alert[] {
  return alerts.slice().sort((a, b) => {
    if (a.category !== b.category) return a.category === "meteorological" ? -1 : 1;
    if (b.severity !== a.severity) return b.severity - a.severity;
    return new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime();
  });
}

// Purely time-ordered — used for "moje alerty", where the point is to see
// what's coming up next, not to triage by severity first.
export function sortAlertsChronologically(alerts: Alert[]): Alert[] {
  return alerts.slice().sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());
}

export type AlertStatus = "nadchodzące" | "aktywne" | "zakończone";

export function alertStatus(alert: Alert, now: Date): AlertStatus {
  if (now < new Date(alert.starts_at)) return "nadchodzące";
  if (now <= new Date(alert.ends_at)) return "aktywne";
  return "zakończone";
}

export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (date.getUTCFullYear() >= 9999) {
    return "do odwołania";
  }
  return date.toLocaleString("pl-PL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

// Skips repeating the date when a range starts and ends on the same day
// (e.g. "15.07, 12:00 → 17:00" instead of "15.07, 12:00 → 15.07, 17:00").
export function formatDateRange(startIso: string, endIso: string): string {
  const end = new Date(endIso);
  if (end.getUTCFullYear() >= 9999) {
    return `${formatDateTime(startIso)} → do odwołania`;
  }

  const start = new Date(startIso);
  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();
  if (!sameDay) {
    return `${formatDateTime(startIso)} → ${formatDateTime(endIso)}`;
  }

  const datePart = start.toLocaleString("pl-PL", { day: "2-digit", month: "2-digit" });
  const startTime = start.toLocaleString("pl-PL", { hour: "2-digit", minute: "2-digit" });
  const endTime = end.toLocaleString("pl-PL", { hour: "2-digit", minute: "2-digit" });
  return `${datePart}, ${startTime} → ${endTime}`;
}
